import { useState } from "react";
import {
  Activity,
  Bell,
  ChevronDown,
  Grid2X2,
  LineChart,
  Settings,
  ShoppingBag,
} from "lucide-react";

import { CrawlPolicy } from "@/components/crawl-policy";
import { InsightsPanel } from "@/components/insights-panel";
import { PriceHistoryChart } from "@/components/price-history-chart";
import { ProductOverview } from "@/components/product-overview";
import { ProductRail } from "@/components/product-rail";
import { ProductSearch } from "@/components/product-search";
import { SnapshotTable } from "@/components/snapshot-table";
import { Button } from "@/components/ui/button";
import { TooltipProvider } from "@/components/ui/tooltip";
import { resolveProductSearch } from "@/lib/product-search";
import { cn } from "@/lib/utils";
import { trackedProducts, type Product } from "@/mocks/products";

const navItems = [
  { label: "Overview", icon: <Grid2X2 className="h-5 w-5" />, active: false },
  { label: "Price history", icon: <LineChart className="h-5 w-5" />, active: true },
  { label: "Alerts", icon: <Bell className="h-5 w-5" />, active: false },
  { label: "Settings", icon: <Settings className="h-5 w-5" />, active: false },
];

export default function App(): JSX.Element {
  const [selectedProduct, setSelectedProduct] = useState<Product>(trackedProducts[0]);
  const [query, setQuery] = useState(trackedProducts[0].canonicalUrl);
  const [notice, setNotice] = useState("Mock mode. Data is local and reset on refresh.");

  const handleSearch = (): void => {
    const resolution = resolveProductSearch(query);
    setSelectedProduct(resolution.product);
    setNotice(resolution.message);
  };

  const handleProductSelect = (product: Product): void => {
    setSelectedProduct(product);
    setQuery(product.canonicalUrl);
    setNotice("Interest recorded. Latest cached history is shown.");
  };

  return (
    <TooltipProvider>
      <main className="min-h-screen bg-[#020712] text-white">
        <div className="min-h-screen bg-[radial-gradient(circle_at_58%_18%,rgba(30,76,135,0.18),transparent_28%),radial-gradient(circle_at_86%_78%,rgba(14,78,140,0.12),transparent_33%)]">
          <div className="grid min-h-screen xl:grid-cols-[282px_minmax(0,1fr)]">
            <aside className="hidden border-r border-[#1f2d44] bg-[#050b15]/95 xl:grid xl:grid-rows-[76px_minmax(0,1fr)]">
              <BrandHeader />
              <div className="grid min-h-0 grid-cols-[52px_minmax(0,1fr)]">
                <IconRail />
                <div className="grid min-h-0 grid-rows-[minmax(0,1fr)_92px] border-l border-[#1f2d44] px-2 py-8">
                  <ProductRail
                    onSelect={handleProductSelect}
                    products={trackedProducts}
                    selectedKey={selectedProduct.productKey}
                  />
                  <TrackingStatus />
                </div>
              </div>
            </aside>

            <section className="min-w-0">
              <HeaderBar />
              <div className="mx-auto grid w-full max-w-[1170px] gap-5 px-4 pb-7 pt-7 lg:px-8 xl:max-w-none xl:px-[30px]">
                <ProductSearch
                  notice={notice}
                  onQueryChange={setQuery}
                  onQuickSelect={handleProductSelect}
                  onSubmit={handleSearch}
                  query={query}
                  quickProducts={trackedProducts.slice(0, 3)}
                />
                <div className="grid min-w-0 gap-5 xl:grid-cols-[minmax(0,1fr)_292px]">
                  <div className="grid min-w-0 gap-4">
                    <ProductOverview
                      onMockRecrawl={() => {
                        setNotice("Mock recrawl queued. Cooldown remains enforced in this UI.");
                      }}
                      product={selectedProduct}
                    />
                    <PriceHistoryChart snapshots={selectedProduct.snapshots} />
                    <SnapshotTable snapshots={selectedProduct.snapshots} />
                  </div>
                  <div className="grid content-start gap-14">
                    <CrawlPolicy product={selectedProduct} />
                    <InsightsPanel product={selectedProduct} />
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
    </TooltipProvider>
  );
}

function BrandHeader(): JSX.Element {
  return (
    <div className="flex items-center gap-4 border-b border-[#1f2d44] px-5">
      <span className="grid h-9 w-9 place-items-center rounded-[7px] bg-[#ff4a12] text-white shadow-[0_0_24px_rgba(255,74,18,0.38)]">
        <ShoppingBag className="h-6 w-6" />
      </span>
      <span className="grid">
        <strong className="text-base font-extrabold leading-5">Shopee VN</strong>
        <span className="text-sm leading-5 text-[#8f9bb0]">Price Intelligence</span>
      </span>
    </div>
  );
}

function HeaderBar(): JSX.Element {
  return (
    <header className="flex min-h-[76px] items-center justify-between gap-4 border-b border-[#1f2d44] px-4 lg:px-8">
      <div className="grid gap-1">
        <h1 className="text-2xl font-extrabold leading-7">Price History</h1>
        <p className="text-sm font-medium text-[#9aa7bb]">Track. Analyze. Save more.</p>
      </div>
      <div className="flex items-center gap-2">
        <Button
          className="h-9 gap-2 rounded-md border-[#22324c] bg-[#050b15] px-3 text-sm font-bold text-white hover:bg-[#0a1727]"
          type="button"
          variant="outline"
        >
          Local FE
          <ChevronDown className="h-4 w-4" />
        </Button>
        <Button
          className="h-9 rounded-md border-[#22324c] bg-[#050b15] px-4 text-sm font-bold text-white hover:bg-[#0a1727]"
          type="button"
          variant="outline"
        >
          Mocks
        </Button>
      </div>
    </header>
  );
}

function IconRail(): JSX.Element {
  return (
    <nav className="relative flex flex-col items-center gap-7 py-9" aria-label="Dashboard">
      {navItems.map((item, index) => (
        <button
          className={cn(
            "relative grid h-8 w-8 place-items-center rounded-md text-[#9aa7bb] transition hover:text-white",
            item.active && "text-[#ff4a12]",
          )}
          aria-label={item.label}
          key={index}
          type="button"
        >
          {item.active ? (
            <span className="absolute -left-[11px] h-8 w-0.5 rounded-full bg-[#ff4a12]" />
          ) : null}
          {item.icon}
        </button>
      ))}
    </nav>
  );
}

function TrackingStatus(): JSX.Element {
  return (
    <div className="mb-2 grid content-center gap-2 rounded-lg border border-[#20304a] bg-[#071120] p-4">
      <div className="flex items-center justify-between gap-3">
        <span className="inline-flex items-center gap-2 text-sm font-extrabold text-white">
          <Activity className="h-5 w-5 text-[#35f36c]" />
          Tracking active
        </span>
        <span className="h-5 w-5 rounded-full border border-[#1b6634] bg-[#0a2d18] shadow-[0_0_18px_rgba(53,243,108,0.55)]" />
      </div>
      <p className="text-xs font-medium text-[#9aa7bb]">
        4 products - Auto-updates enabled
      </p>
    </div>
  );
}
